import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import UserIcon from '../../../icons/User'
import { useHasAdminManagementAccess } from '../../../services/auth/permissions/usePermissions'
import useAuth from '../../../services/auth/useAuth'

const AccountPopover = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  const hasAdminManagementAccess = useHasAdminManagementAccess()

  const handleOpen = (): void => {
    setOpen(true)
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose()
      await logout()
      navigate('/login', { replace: true, state: { loggedOut: true } })
    } catch (err) {
      console.error(err)
      enqueueSnackbar(t('errors.logout'), {
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top',
        },
        variant: 'error',
      })
    }
  }

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Avatar
          id={'profile-avatar'}
          // src={user.avatar}
          sx={{
            height: 32,
            width: 32,
          }}
        />
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 240 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography color='textPrimary' variant='subtitle1'>
            {user?.fullName || user?.email}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ mt: 2 }}>
          {hasAdminManagementAccess && (
            <>
              <MenuItem
                component={RouterLink}
                to='/admin/admins'
                onClick={handleClose}
              >
                <ListItemIcon>
                  <UserIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography color='textPrimary' variant='subtitle2'>
                      {t('adminConsole.titles.admins')}
                    </Typography>
                  }
                />
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to='/admin/roles'
                onClick={handleClose}
              >
                <ListItemIcon>
                  <UserIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography color='textPrimary' variant='subtitle2'>
                      {t('adminConsole.titles.roles')}
                    </Typography>
                  }
                />
              </MenuItem>
            </>
          )}
          <MenuItem
            component={RouterLink}
            to='/admin/profile'
            onClick={handleClose}
          >
            <ListItemIcon>
              <UserIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color='textPrimary' variant='subtitle2'>
                  {t('adminConsole.titles.myProfile')}
                </Typography>
              }
            />
          </MenuItem>
        </Box>
        <Box sx={{ p: 2 }}>
          <Button
            color='primary'
            fullWidth
            onClick={handleLogout}
            variant='outlined'
          >
            {t('navigation.logout')}
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default AccountPopover
